import { z } from 'zod'

import {
  CreateBrandedEnum,
  createEnumMap,
  type ZodInfer,
} from '@mntn-dev/utility-types'

/** All UBS invoice status values as string literals, ordered by their 1-based ID. */
export const UbsInvoiceStatusStrings = [
  /* UbsInvoiceStatusId  1 */ 'pending',
  /* UbsInvoiceStatusId  2 */ 'approved',
  /* UbsInvoiceStatusId  3 */ 'due',
  /* UbsInvoiceStatusId  4 */ 'overdue',
  /* UbsInvoiceStatusId  5 */ 'paid',
  /* UbsInvoiceStatusId  6 */ 'voided',
  /* UbsInvoiceStatusId  7 */ 'refunded',
  /* UbsInvoiceStatusId  8 */ 'requested',
  /* UbsInvoiceStatusId  9 */ 'under_review',
  /* UbsInvoiceStatusId 10 */ 'scheduled',
  /* UbsInvoiceStatusId 11 */ 'rejected',
  /* UbsInvoiceStatusId 12 */ 'canceled',
] as const

/**
 * - `UbsInvoiceStatuses` — readonly array of all branded status values.
 * - `UbsInvoiceStatusSchema` — Zod schema that parses a string into a branded `UbsInvoiceStatus`.
 * - `UbsInvoiceStatusEnum` — frozen object keyed by each status for dot-access (e.g. `UbsInvoiceStatusEnum.pending`).
 * - `unbrandUbsInvoiceStatus` — strips the brand, recovering the original string literal union.
 */
export const [
  UbsInvoiceStatuses,
  UbsInvoiceStatusSchema,
  UbsInvoiceStatusEnum,
  unbrandUbsInvoiceStatus,
] = CreateBrandedEnum({ brand: 'UbsInvoiceStatus' }, ...UbsInvoiceStatusStrings)

/** Branded union of all UBS invoice status string literals. */
export type UbsInvoiceStatus = ZodInfer<typeof UbsInvoiceStatusSchema>

/** Zod schema for a branded 1-based integer ID corresponding to a UBS invoice status. */
export const UbsInvoiceStatusIdSchema = z
  .number()
  .int()
  .positive()
  .max(UbsInvoiceStatuses.length)
  .brand('ubs-invoice-status-id')

/** Branded 1-based integer type identifying a UBS invoice status. */
export type UbsInvoiceStatusId = ZodInfer<typeof UbsInvoiceStatusIdSchema>

/** Parses a number into a branded {@link UbsInvoiceStatusId}. Throws if out of range. */
export const UbsInvoiceStatusId = (value: number) => {
  return UbsInvoiceStatusIdSchema.parse(value)
}

/**
 * - `UbsInvoiceStatusMap` — maps each status string to its 1-based {@link UbsInvoiceStatusId} (e.g. `UbsInvoiceStatusMap.pending` → `1`).
 * - `ubsInvoiceStatusFromId` — converts a 1-based integer back to its status string. Throws if out of range.
 */
export const [UbsInvoiceStatusMap, ubsInvoiceStatusFromId] = createEnumMap(
  [...UbsInvoiceStatusStrings],
  UbsInvoiceStatusId
)

/** Converts a {@link UbsInvoiceStatusId} or status string into a branded {@link UbsInvoiceStatus}. */
export const UbsInvoiceStatus = (
  value: UbsInvoiceStatusId | ZodInfer<typeof UbsInvoiceStatusSchema>
) => {
  return UbsInvoiceStatusSchema.parse(
    typeof value === 'string' ? value : ubsInvoiceStatusFromId(value)
  )
}

/**
 * Converts a branded {@link UbsInvoiceStatus} into its branded numeric {@link UbsInvoiceStatusId}.
 * Example: `ubsInvoiceStatusToId(UbsInvoiceStatusEnum.approved)` => `2`.
 */
export const ubsInvoiceStatusToId = (status: UbsInvoiceStatus) =>
  UbsInvoiceStatusMap[unbrandUbsInvoiceStatus(status)]
